/**
 * Shared plumbing for rendering agent-authored HTML posters inside sandboxed
 * iframes. Both the research item page (framed, height-synced) and the
 * full-viewport poster route (raw, natively scrolling) build on these helpers
 * so the security posture and message contract stay in one place.
 */
import { useEffect, useState } from 'react';

/**
 * SECURITY (load-bearing): posters are agent-authored HTML rendered in a
 * sandboxed iframe WITHOUT `allow-same-origin`. The srcdoc document gets a
 * unique opaque origin, so its scripts cannot reach the parent DOM, cookies, or
 * the OAuth tokens in localStorage. NEVER add `allow-same-origin`.
 *
 * `allow-popups-to-escape-sandbox` is safe and is NOT a relaxation of that
 * rule: it governs only the documents a popup opens, not the poster itself. A
 * poster link opened with `target="_blank"` becomes a normal top-level document
 * at the DESTINATION's own origin (with its own cookies and storage) instead of
 * inheriting the poster's sandbox flags and rendering broken. The poster
 * document keeps its opaque origin and still cannot reach the app origin.
 */
export const POSTER_SANDBOX = 'allow-scripts allow-popups allow-popups-to-escape-sandbox';

/** postMessage `type` the injected poster-measurement script reports height under. */
export const POSTER_HEIGHT_MESSAGE = 'almanac-poster-height';

/**
 * postMessage `type` the parent sends to toggle the poster's own overflow
 * between clipped (measured-height mode) and scrollable (fullscreen mode).
 */
export const POSTER_SCROLL_MODE_MESSAGE = 'almanac-poster-scroll-mode';

/** Minimum rendered poster height (px) before the measured value takes over. */
export const MIN_POSTER_HEIGHT = 320;

/**
 * Upper bound (px) for poster-controlled layout expansion. A long explainer at
 * phone width measures well past 20,000px, so the bound is generous; it only
 * exists to keep a hostile poster from growing the page without limit.
 */
export const MAX_POSTER_HEIGHT = 100_000;

/**
 * Turn a height report from the measure script into the framed iframe height,
 * or `null` when the payload is not a usable number. The value is clamped into
 * `[MIN_POSTER_HEIGHT, MAX_POSTER_HEIGHT]` rather than rejected above the cap:
 * dropping an over-cap report left the frame at its minimum height, which
 * showed only the first screen of a long document.
 */
export function framedPosterHeight(height: unknown): number | null {
  if (typeof height !== 'number' || !Number.isFinite(height)) return null;
  return Math.min(MAX_POSTER_HEIGHT, Math.max(MIN_POSTER_HEIGHT, height + POSTER_HEIGHT_BUFFER));
}

/**
 * Buffer (px) added to the measured height so a residual sub-pixel overflow
 * never triggers the iframe's own scrollbar (the "double scroll" bug).
 */
export const POSTER_HEIGHT_BUFFER = 2;

/**
 * Script injected into the sandboxed poster srcdoc to report its content height
 * to the parent and to honor scroll-mode toggles. The srcdoc has a null
 * (opaque) origin, so the target origin MUST be '*' — there is no concrete
 * origin to address. Nothing sensitive is disclosed: the content is already
 * fully attacker-controlled. The parent verifies inbound reports by
 * source + type; this script likewise verifies scroll-mode messages come from
 * `window.parent` and carry the expected type before acting.
 *
 * Height is reported as the ceiling of the max of the document and body scroll
 * heights so a fractional-pixel content height can never round down and leave a
 * sliver of internal overflow.
 */
const POSTER_MEASURE_SCRIPT = `<script>
(function () {
  function report() {
    var body = document.body;
    var height = Math.ceil(
      Math.max(
        document.documentElement.scrollHeight,
        body ? body.scrollHeight : 0
      )
    );
    parent.postMessage({ type: '${POSTER_HEIGHT_MESSAGE}', height: height }, '*');
  }
  window.addEventListener('load', report);
  if (typeof ResizeObserver !== 'undefined') {
    var observer = new ResizeObserver(report);
    observer.observe(document.documentElement);
    if (document.body) observer.observe(document.body);
  }
  window.addEventListener('message', function (event) {
    if (event.source !== window.parent) return;
    var data = event.data;
    if (typeof data !== 'object' || data === null) return;
    if (data.type !== '${POSTER_SCROLL_MODE_MESSAGE}') return;
    document.documentElement.style.overflowY = data.scroll === true ? 'auto' : 'hidden';
  });
  report();
})();
</script>`;

/**
 * Script injected into every poster surface so an outbound link opens in a new
 * browser tab instead of navigating the iframe (which strands the reader inside
 * the poster frame, with browser-back as the only escape — and Almanac runs as
 * an installed PWA, where there is no URL bar to recover with).
 *
 * A capture-phase listener on `document` rewrites the anchor's `target`/`rel`
 * before the navigation is dispatched, so it wins over whatever target the
 * agent-authored poster set on the link itself; existing `rel` tokens are kept
 * and `noopener noreferrer` is added alongside them. In-page fragment links keep
 * working in-frame; so do non-http(s) schemes (`mailto:`, `tel:`), which are
 * handed to the OS anyway.
 *
 * Protocol is read from the anchor's resolved `protocol` property rather than a
 * regex — a regex literal inside this template string would have its escapes
 * eaten before it reaches the browser.
 */
const POSTER_LINK_TARGET_SCRIPT = `<script>
(function () {
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    var anchor = target.closest('a[href]');
    if (!anchor) return;
    var href = anchor.getAttribute('href') || '';
    if (href.charAt(0) === '#') return;
    if (anchor.protocol !== 'http:' && anchor.protocol !== 'https:') return;
    anchor.target = '_blank';
    // Preserve whatever rel tokens the poster author set (nofollow, ugc, ...)
    // and add only the ones the new tab needs.
    anchor.relList.add('noopener');
    anchor.relList.add('noreferrer');
  }, true);
})();
</script>`;

/**
 * Append the outbound-link handler to a poster document.
 *
 * Trailing script content is placed by browsers into the parsed document, which
 * avoids corrupting a `</body>` marker inside an allowed inline script or
 * comment in the poster body.
 *
 * @param html Agent-authored poster HTML (already trusted only inside the sandbox).
 * @returns The srcdoc string to feed the sandboxed iframe.
 */
export function withPosterLinkTargets(html: string): string {
  return `${html}${POSTER_LINK_TARGET_SCRIPT}`;
}

/**
 * Clip the poster's own vertical overflow by default. Removable at runtime via
 * a {@link POSTER_SCROLL_MODE_MESSAGE} postMessage (used for fullscreen, where
 * the poster must scroll internally). Horizontal behavior is left untouched —
 * legacy posters are responsive.
 */
const POSTER_CLIP_STYLE = '<style>html{overflow-y:hidden}</style>';

/**
 * Build the srcdoc for the framed, height-synced poster surfaces: the raw
 * poster HTML followed by the outbound-link handler, the clip style, and the
 * measure script.
 *
 * Trailing script/style content is placed by browsers into the parsed document,
 * which avoids corrupting a `</body>` marker inside an allowed inline script or
 * comment in the poster body.
 *
 * @param html Agent-authored poster HTML (already trusted only inside the sandbox).
 * @returns The srcdoc string to feed the sandboxed iframe.
 */
export function withPosterMeasureScript(html: string): string {
  return `${withPosterLinkTargets(html)}${POSTER_CLIP_STYLE}${POSTER_MEASURE_SCRIPT}`;
}

/**
 * The theme the poster iframe should render under, pinned explicitly.
 *
 * An opaque-origin (sandboxed srcdoc) document derives its
 * `prefers-color-scheme` from the used `color-scheme` of the FRAME ELEMENT in
 * the embedding page. The app declares `color-scheme: light dark` via
 * stylesheet, and the srcdoc can load before that stylesheet resolves — so
 * without an explicit value the poster's theme is a coin flip per load. Set
 * the returned value as the iframe's inline `colorScheme` style to make it
 * deterministic; it tracks the media query live.
 */
export function usePosterColorScheme(): 'dark' | 'light' {
  const [scheme, setScheme] = useState<'dark' | 'light'>(() => {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    function onChange(event: MediaQueryListEvent) {
      setScheme(event.matches ? 'dark' : 'light');
    }
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return scheme;
}
